import { ReactElement, useCallback, useMemo } from 'react'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { Box, IconButton } from '@mui/material'
import type { GridColDef, GridRenderCellParams, GridRowModel } from '@mui/x-data-grid'
import { DataGrid, gridClasses } from '@mui/x-data-grid'
import { createDefaultHeaderRow } from 'src/domain/planTables/defaults'
import type { TableData, TableRow } from 'src/domain/planTables/types'

interface GridRow extends GridRowModel {
  id: string
  rowKey: string
  isHeader: boolean
}

interface Props {
  table: TableData
  onTableChange: (updater: (table: TableData) => TableData) => void
  readOnly?: boolean
}

const PlanTableDataGrid = ({ table, onTableChange, readOnly = false }: Props): ReactElement => {
  const rows = useMemo<GridRow[]>(() => {
    const headerRow =
      table.headerRow ?? createDefaultHeaderRow(table.columns.map(column => column.key))
    return [
      {
        id: headerRow.key,
        rowKey: headerRow.key,
        isHeader: true,
        ...Object.fromEntries(
          headerRow.cells.map(cell => [cell.columnKey, cell.value.join('\n') ?? '']),
        ),
      },
      ...table.rows.map(row => ({
        id: row.key,
        rowKey: row.key,
        isHeader: false,
        ...Object.fromEntries(row.cells.map(cell => [cell.columnKey, cell.value.join('\n') ?? ''])),
      })),
    ]
  }, [table])

  const buildTableRow = useCallback(
    (gridRow: GridRow, columns: TableData['columns']): TableRow => ({
      key: gridRow.rowKey,
      cells: columns.map(column => ({
        columnKey: column.key,
        value: [String(gridRow[column.key] ?? '')],
      })),
    }),
    [],
  )

  const processRowUpdate = useCallback(
    (newRow: GridRow) => {
      onTableChange(currentTable => {
        if (newRow.isHeader) {
          return {
            ...currentTable,
            headerRow: buildTableRow(newRow, currentTable.columns),
          }
        }

        return {
          ...currentTable,
          rows: currentTable.rows.map(row =>
            row.key === newRow.rowKey ? buildTableRow(newRow, currentTable.columns) : row,
          ),
        }
      })
      return newRow
    },
    [buildTableRow, onTableChange],
  )

  const handleDeleteRow = useCallback(
    (rowKey: string) => {
      onTableChange(currentTable => ({
        ...currentTable,
        rows: currentTable.rows.filter(row => row.key !== rowKey),
      }))
    },
    [onTableChange],
  )

  const columns = useMemo<GridColDef<GridRow>[]>(
    () =>
      [
        ...table.columns.map((column, index) => ({
          field: column.key,
          headerName: column.header ?? `Spalte ${index + 1}`,
          flex: 1,
          minWidth: 80,
          editable: !readOnly,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
        })),
        {
          field: 'actions',
          headerName: '',
          width: 44,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          editable: false,
          renderCell: (params: GridRenderCellParams<GridRow>) =>
            readOnly || params.row.isHeader ? null : (
              <IconButton
                size="small"
                aria-label="Zeile entfernen"
                onClick={() => handleDeleteRow(params.row.rowKey)}
                data-cy={`plan-table-delete-row-${params.row.rowKey}`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            ),
        },
      ].filter(column => (readOnly ? column.field !== 'actions' : true)),
    [handleDeleteRow, readOnly, table.columns],
  )

  return (
    <Box sx={{ height: 240, width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        density="compact"
        hideFooter
        editMode={readOnly ? undefined : 'cell'}
        disableRowSelectionOnClick
        processRowUpdate={readOnly ? undefined : processRowUpdate}
        onProcessRowUpdateError={() => undefined}
        getRowClassName={params => (params.row.isHeader ? 'plan-table-header-row' : '')}
        sx={{
          [`& .${gridClasses.cell}`]: { fontSize: 12 },
          [`& .plan-table-header-row`]: {
            fontWeight: 600,
            backgroundColor: theme => theme.palette.action.hover,
          },
        }}
        data-cy="plan-table-data-grid"
      />
    </Box>
  )
}

export default PlanTableDataGrid
